import { ReactNode } from 'react';
import { Box, Link as MuiLink, Typography, useMediaQuery, type Theme } from '@mui/material';
import { Link } from 'react-router-dom';
import { getMediaSize } from '@/util/media';
import { publicAssetUrl } from '@/util/public-url';
import { useHost } from '../../hooks/use-host';
import { CookieSettingsLink } from '../../components/cookie-settings-link';
import { T } from '../../util/Localize';

const HEADER_HEIGHT = 68;
const DEFAULT_BACKGROUND_URL = publicAssetUrl('images/login-default-background.webp');

/**
 * Shown for a link with no brand override configured (UX-696 owns the override system itself — a
 * `HostInfo` field per link). These are the platform's own legal pages, the same ones linked from
 * the hdphotohub.com footer.
 */
const PLATFORM_DEFAULT_LINKS = {
    privacyPolicyUrl: 'https://hdphotohub.com/learn-privacy',
    termsUrl: 'https://hdphotohub.com/learn-terms',
    disclaimerUrl: 'https://hdphotohub.com/learn-disclaimer',
    cookiePolicyUrl: 'https://hdphotohub.com/learn-cookies'
};

/** A brand override of exactly this means "do not render this link at all" (API-549). */
const LINK_SUPPRESSED = '-';

/**
 * Resolves one footer legal link's tri-state (the API never resolves it — see `HostInfo`):
 * absent/null/empty gives the platform default, a literal `'-'` suppresses the link, and
 * anything else is the brand's own URL. `null` return means render NOTHING for that link —
 * not a disabled or empty anchor.
 *
 * `brandUrl || platformDefault` would send a suppressed link out with `href="-"`, which is why
 * all four go through here rather than falling back inline.
 */
function resolveLegalLink(
    brandUrl: string | null | undefined,
    platformDefault: string
): string | null {
    const override = brandUrl?.trim();
    if (!override) {
        return platformDefault;
    }
    return override === LINK_SUPPRESSED ? null : override;
}

/**
 * The top bar of the login page and its related screens: the brand's logo (or its name when it has
 * none) on the menu background colour. Exported so the signed-out 404 page (UX-910) renders the
 * very same bar rather than a copy that can drift — logo size and background included.
 *
 * `end` sits at the right-hand edge: the 404 page puts the delivery shell's profile menu there for
 * a signed-in viewer. The login screens pass nothing.
 */
export function AuthPageHeader({ end }: { end?: ReactNode } = {}) {
    const { data: hostInfo } = useHost();
    const logoUrl = hostInfo?.logo ? getMediaSize(hostInfo.logo, { width: 600 })?.url : null;

    return (
        <Box
            component="header"
            sx={{
                // A minimum, not a fixed height, so a larger logo grows the header.
                minHeight: HEADER_HEIGHT,
                py: 1.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                px: { xs: 2, sm: 5 },
                // UX-696: "header background should use the menu background color" — same
                // variable and fallback the Studio nav header itself renders against.
                bgcolor: 'var(--menu-background, #fff)',
                borderBottom: '0.5px solid var(--grey-300, #e0e0e0)',
                // The profile menu's backdrop is portalled at modal z-index; lift the bar holding
                // the menu above it while it is open (same fix as delivery's top bars).
                position: 'relative',
                '&:has([data-profile-menu-open="true"])': {
                    zIndex: theme => theme.zIndex.modal + 1
                }
            }}
        >
            <Link to={hostInfo?.headerUrl ?? '/'}>
                {logoUrl ? (
                    <img
                        src={logoUrl}
                        alt={hostInfo?.brandName ?? ''}
                        style={{
                            width: 'var(--login-logo-width, 180px)',
                            height: 'auto',
                            display: 'block'
                        }}
                    />
                ) : (
                    <Typography sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'text.primary' }}>
                        {hostInfo?.brandName ?? ''}
                    </Typography>
                )}
            </Link>
            {end ? (
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{end}</Box>
            ) : null}
        </Box>
    );
}

/**
 * The frame shared by the login page and its related screens (registration, password reset,
 * set password — UX-696): a header with the brand mark, a background image with a panel on
 * top, and a footer with legal links. Only the panel CONTENT differs per screen, passed as
 * `children`.
 *
 * ONE PIECE OF UX-696 IS DELIBERATELY NOT BUILT: the header's "Order button/s" for the brand's
 * Public carts. That data has no path to this page today — `carts/orderforms` is an
 * authenticated admin endpoint (`OrderFormController.cs`, `[Authorize]`) that a logged-out
 * visitor cannot call, and the public host-bootstrap endpoint this layout already reads
 * everything else from (`GET /brands/host/{hostname}`, → `useHost()`) returns nothing
 * cart-related. The ticket's own wording is also ambiguous about the multi-cart case ("cart
 * button/s" vs. "the name of the *selected* cart" implies a selection concept the ticket never
 * defines) — building against a guessed shape for both the API and the interaction seemed worse
 * than surfacing it plainly. Everything else in the ticket (header background/brand mark,
 * background image with platform-default fallback, panel opacity, footer link overrides and
 * the copyright toggle) is implemented below.
 */
export function AuthPageLayout({ children }: { children: ReactNode }) {
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const { data: hostInfo } = useHost();

    // `loginBackgrounds` holds at most one row today, but it is a list by contract — take the
    // first rather than treating it as a scalar.
    // No fallback to the studio `bodyBackground`: an absent login image shows the default texture.
    const backgroundMedia = hostInfo?.loginBackgrounds?.[0];
    const backgroundUrl = backgroundMedia
        ? getMediaSize(backgroundMedia, { width: 1920 })?.url
        : null;

    const legalLinks = [
        {
            label: 'Privacy Policy',
            href: resolveLegalLink(
                hostInfo?.privacyPolicyUrl,
                PLATFORM_DEFAULT_LINKS.privacyPolicyUrl
            )
        },
        {
            label: 'Terms',
            href: resolveLegalLink(hostInfo?.termsUrl, PLATFORM_DEFAULT_LINKS.termsUrl)
        },
        {
            label: 'Disclaimer',
            href: resolveLegalLink(hostInfo?.disclaimerUrl, PLATFORM_DEFAULT_LINKS.disclaimerUrl)
        },
        {
            label: 'Cookie Policy',
            href: resolveLegalLink(
                hostInfo?.cookiePolicyUrl,
                PLATFORM_DEFAULT_LINKS.cookiePolicyUrl
            )
        }
    ].filter((link): link is { label: string; href: string } => link.href !== null);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
            <AuthPageHeader />
            <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        // `cover` centers and scales to fill while preserving aspect ratio,
                        // cropping whichever axis overflows — exactly the ticket's rule, and it
                        // never stretches or letterboxes.
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        ...(backgroundUrl
                            ? {
                                  backgroundColor: 'var(--background-body, #f6f8f9)',
                                  backgroundImage: `url(${backgroundUrl})`
                              }
                            : {
                                  // Platform default texture: white-on-transparent streaks laid
                                  // over the brand's primary colour, so each brand gets its own
                                  // colour behind the same texture.
                                  backgroundColor: 'var(--primary-main, #1565c0)',
                                  backgroundImage: `url(${DEFAULT_BACKGROUND_URL})`
                              })
                    }}
                />
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        width: '100%',
                        justifyContent: isMobile ? 'stretch' : 'flex-end'
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            width: isMobile ? '100%' : 500,
                            maxWidth: '100%',
                            overflowY: 'auto'
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                bgcolor: '#000',
                                opacity: 'var(--login-panel-opacity, 60%)'
                            }}
                        />
                        <Box
                            sx={{
                                position: 'relative',
                                minHeight: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 4,
                                p: { xs: 3, sm: 5 },
                                color: '#fff'
                            }}
                        >
                            {/* Centers the screen's content in the space above the footer; on a short viewport the panel grows and scrolls instead. */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                {children}
                            </Box>
                            <Box
                                component="footer"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    textAlign: 'center'
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        gap: 2.5,
                                        fontSize: 12
                                    }}
                                >
                                    {legalLinks.map(link => (
                                        <MuiLink
                                            key={link.label}
                                            href={link.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ color: 'inherit' }}
                                        >
                                            {T(link.label)}
                                        </MuiLink>
                                    ))}
                                    <CookieSettingsLink sx={{ color: 'inherit', fontSize: 'inherit' }} />
                                </Box>
                                {!hostInfo?.disableFooterCopyright && (
                                    <Typography sx={{ color: 'inherit', fontSize: 10, opacity: 0.8 }}>
                                        {`© 2017-${new Date().getFullYear()} HDPhotoHub.com. `}
                                        {T('All rights reserved.')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
